import { pickFile, pickFiles, naturalSortFiles } from '@/core/lib/files'
import { isAllowedType, readableUploadTypes } from '@/core/lib/fileTypes'
import { useDropZone, useMultiDropZone } from '@/core/hooks/useDropZone'
import { UploadField, toastError } from 'admination-react'

export function DropCard({
  title,
  body,
  accept,
  onFile,
  onFiles,
  multiple = false,
  loading = false,
  loadingLabel,
  typeError,
  testId,
  invalid = false,
  disabled = false,
}: {
  title: string
  body: string
  accept: readonly string[]
  onFile?: (file: File) => void
  onFiles?: (files: File[]) => void
  multiple?: boolean
  loading?: boolean

  loadingLabel: string

  typeError?: (file: File) => string

  testId?: string
  invalid?: boolean
  disabled?: boolean
}) {
  const rejectType = (file: File) =>
    toastError(typeError ? typeError(file) : `Dateiformat nicht unterstützt – bitte ${readableUploadTypes(accept)} hochladen.`)

  const guarded = (file: File) => {
    if (loading || disabled) return
    if (isAllowedType(file, accept)) onFile?.(file)
    else rejectType(file)
  }

  const guardedBatch = (incoming: File[]) => {
    if (loading || disabled || incoming.length === 0) return
    const wrongType = incoming.find((f) => !isAllowedType(f, accept))
    if (wrongType) {
      rejectType(wrongType)
      return
    }
    onFiles?.(naturalSortFiles(incoming))
  }

  const single = useDropZone(guarded)
  const multi = useMultiDropZone(guardedBatch)
  const { isDragOver, handlers } = multiple ? multi : single

  return (
    <UploadField
      className={multiple ? 'dropzone-card' : undefined}
      title={title}
      description={body}
      loading={loading}
      loadingLabel={loadingLabel}
      dragOver={isDragOver}
      invalid={invalid}
      disabled={disabled}
      data-testid={testId}
      onClick={() => {
        if (loading || disabled) return
        if (multiple) {
          void pickFiles(accept.join(',')).then((picked) => guardedBatch(picked))
        } else {
          void pickFile(accept.join(',')).then((f) => {
            if (f) guarded(f)
          })
        }
      }}
      {...handlers}
    />
  )
}
